import { useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { plausibleEventClass, trackEvent } from '../lib/plausible';
import { Button } from './Button';
import { useEventState } from '../lib/eventState';

/**
 * Gamerhood Archive — final home-page section.
 *
 * Three stacked blocks in one section:
 *   1. Red translucent banner (rounded top) with `GAMERHOOD ARCHIVE_` heading
 *      (cyan underscore) + intro paragraph
 *   2. Seasons 1–4 showreel — a click-to-load YouTube facade (`<ShowreelEmbed>`).
 *      The page ships with just the video thumbnail + a play badge; no YouTube
 *      JS or cookies load until the user clicks play. This keeps the home page
 *      free of third-party tracking on load, matching the site's default-denied
 *      consent posture. See ShowreelEmbed for the swap-to-iframe detail.
 *   3. Lilac follow-up description + "Gamerhood Playlist" CTA pointing at
 *      the State Farm YouTube playlist (external, opens in new tab).
 *
 * The Playlist CTA is tagged with `Playlist Click` for adblock resilience
 * on top of Plausible's outbound-links auto-event.
 *
 * Typography mirrors the SfGaming section: dual clamp(min, vw, max) per
 * element, one mobile clamp + one md+ clamp meeting at md (768px) for a
 * seamless transition.
 */
const PLAYLIST_URL = 'https://www.youtube.com/@statefarm/playlists';

// Seasons 1–4 showreel. youtube-nocookie.com (privacy-enhanced mode) + the
// click-to-load facade below means YouTube isn't contacted until the user
// opts in by clicking play.
const SHOWREEL_VIDEO_ID = 'bjf_6fCBJIc';
const SHOWREEL_TITLE = 'Gamerhood seasons 1–4 showreel';

export default function ArchiveSection() {
  const reduce = useReducedMotion();
  // GHS5-88: the post-contest face swaps the intro copy (ticket text verbatim).
  // Same `contestEnded` gate as the other home-page contest-end updates; fails
  // toward the contest copy while /event/state loads (see eventState.tsx).
  const { contestEnded } = useEventState();

  return (
    <motion.section
      id="archive"
      aria-labelledby="archive-heading"
      className="bg-gradient-to-b from-[#463e57] to-[#31263d] px-7 pb-12 pt-6 sm:p-12 md:p-12"
      initial={reduce ? false : { opacity: 0, y: 24 }}
      whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={reduce ? { duration: 0 } : { duration: 0.4, ease: 'easeOut' }}
    >
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-start gap-12 md:gap-10">
        {/* Banner + video pair, tight 10px gap so the banner reads as the
            cap on the video frame */}
        <div className="flex w-full flex-col gap-[10px]">
          <div className="flex flex-col gap-8 rounded-t-[15px] bg-[rgba(255,6,39,0.7)] px-6 py-5 text-white">
            <h2
              id="archive-heading"
              className="font-[Orbitron] font-normal uppercase leading-[0.95] tracking-[-0.03em] text-[clamp(28px,9vw,36px)] md:text-[clamp(36px,4vw,48px)]"
            >
              <span>
                Gamerhood
                <br aria-hidden="true" />
                Archive
              </span>
              <span className="text-[#00ffb2]">_</span>
            </h2>
            <p className="font-[Arial] leading-[1.35] text-[clamp(16px,5vw,22px)] md:text-[clamp(18px,1.6vw,20px)]">
              {contestEnded ? (
                <>
                  <span className="font-bold">Season 5 Streaming Now</span>
                  <br aria-hidden="true" />
                  Gamerhood is back and bigger than ever! Check out all 5 seasons by
                  clicking the button below to be taken to the State Farm YouTube Playlist.
                </>
              ) : (
                <>
                  <span className="font-bold">Season 5 Coming July 31.</span>
                  <br aria-hidden="true" />
                  Gamerhood is back and bigger than ever! Check out the previous 4 seasons by
                  clicking the button below to be taken to the State Farm YouTube Playlist.
                </>
              )}
            </p>
          </div>

          {/* Click-to-load showreel facade (see ShowreelEmbed). 16:9 aspect so
              the thumbnail + the eventual iframe both scale naturally with
              viewport width across all breakpoints. */}
          <ShowreelEmbed />
        </div>

        <p className="max-w-[650px] font-[Arial] leading-[1.35] text-[#d08dff] text-[clamp(16px,5vw,22px)] md:text-[clamp(18px,1.6vw,20px)]">
          We teamed up with some of your favorite streamers, gamers and content creators to face off
          in a next-level gaming competition.
        </p>

        <Button
          variant="dark"
          href={PLAYLIST_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={`min-w-[200px] ${plausibleEventClass('Playlist Click', { platform: 'YouTube' })}`}
        >
          Gamerhood YouTube Playlist
        </Button>
      </div>
    </motion.section>
  );
}

/**
 * Click-to-load YouTube facade for the seasons 1–4 showreel.
 *
 * Before click: renders only the video thumbnail (a cookieless static image
 * from i.ytimg.com) under a brand-red play badge. No YouTube player JS,
 * iframe, or tracking cookies load — the home page stays free of third-party
 * tracking on load, in line with the default-denied consent posture.
 *
 * On click: fires the `Video Play` Plausible event (we own the click, so no
 * Player JS is needed) and swaps in the real youtube-nocookie.com iframe with
 * autoplay so playback starts immediately from the user's single click.
 */
function ShowreelEmbed() {
  const [loaded, setLoaded] = useState(false);

  if (loaded) {
    return (
      <div className="aspect-video w-full overflow-hidden bg-black">
        <iframe
          className="h-full w-full"
          src={`https://www.youtube-nocookie.com/embed/${SHOWREEL_VIDEO_ID}?autoplay=1&rel=0`}
          title={SHOWREEL_TITLE}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      aria-label={`Play ${SHOWREEL_TITLE}`}
      onClick={() => {
        trackEvent('Video Play', { location: 'archive' });
        setLoaded(true);
      }}
      className="group relative aspect-video w-full overflow-hidden bg-black"
    >
      <img
        src={`https://i.ytimg.com/vi/${SHOWREEL_VIDEO_ID}/maxresdefault.jpg`}
        // maxres is absent on some videos; fall back to the always-present hq frame.
        onError={(e) => {
          e.currentTarget.src = `https://i.ytimg.com/vi/${SHOWREEL_VIDEO_ID}/hqdefault.jpg`;
        }}
        alt=""
        loading="lazy"
        className="h-full w-full object-cover"
      />
      <span className="absolute inset-0 bg-black/20 transition-colors group-hover:bg-black/10" />
      <span className="absolute left-1/2 top-1/2 flex h-[clamp(44px,9vw,68px)] w-[clamp(64px,13vw,98px)] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[14px] bg-[rgba(255,6,39,0.85)] shadow-lg transition-transform group-hover:scale-110">
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-1/2 w-1/2 fill-white">
          <path d="M8 5v14l11-7z" />
        </svg>
      </span>
    </button>
  );
}
